/* Current Round JS */
import React, { useState, useEffect, useRef } from "react";
import matchReference from "../utils/matchReference";
import styles from "./CurrentRoundPage.module.css";
import teamLogos from "../utils/teamLogos";
import { getCurrentRound } from "../utils/roundUtils";
import whistle from "./images/sports_32.png";
import {
  format,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  differenceInDays,
} from "date-fns";
import { toZonedTime } from "date-fns-tz";
import TEAM_INFO from "../utils/teamInfo";

// returns "1st", "2nd", etc.
const formatOrdinal = (num) => {
  const j = num % 10,
    k = num % 100;
  if (j === 1 && k !== 11) return `${num}st`;
  if (j === 2 && k !== 12) return `${num}nd`;
  if (j === 3 && k !== 13) return `${num}rd`;
  return `${num}th`;
};

// Map full team names to their corresponding abbreviations
const teamNameMap = {
  "Adelaide Crows": "ADEL",
  "Brisbane Lions": "BRIS",
  "Carlton Blues": "CARL",
  "Collingwood Magpies": "COLL",
  "Essendon Bombers": "ESS",
  "Fremantle Dockers": "FREM",
  "Geelong Cats": "GEEL",
  "Gold Coast Suns": "GC",
  "GWS Giants": "GWS",
  "Hawthorn Hawks": "HAW",
  "Melbourne Demons": "MELB",
  "North Melbourne Kangaroos": "NM",
  "Port Adelaide Power": "PORT",
  "Richmond Tigers": "RICH",
  "St Kilda Saints": "STK",
  "Sydney Swans": "SYD",
  "West Coast Eagles": "WCE",
  "Western Bulldogs": "WB",
};

const getTeamDisplayName = (team) => {
  const teamNamesMap = {
    Adelaide: "ADEL",
    "Brisbane Lions": "BRIS",
    Carlton: "CARL",
    Collingwood: "COLL",
    Essendon: "ESS",
    Fremantle: "FREM",
    Geelong: "GEEL",
    "Gold Coast": "GC",
    "Greater Western Sydney": "GWS",
    Hawthorn: "HAW",
    Melbourne: "MELB",
    "North Melbourne": "NM",
    "Port Adelaide": "PORT",
    Richmond: "RICH",
    "St Kilda": "STK",
    Sydney: "SYD",
    "West Coast": "WCE",
    "Western Bulldogs": "WB",
  };
  return teamNamesMap[team] || team;
};

const CurrentRoundPage = () => {
  const [currentMatches, setCurrentMatches] = useState([]);
  const [currentRound, setCurrentRound] = useState(0);
  const [useMockData, setUseMockData] = useState(
    () => localStorage.getItem("useMockData") === "true",
  );
  const [selectedTeam, setSelectedTeam] = useState("");
  const refreshInterval = useRef(null);
  const [standingsMap, setStandingsMap] = useState({});
  const useMockDataRef = useRef(useMockData);
  const getSeasonYear = () => new Date().getFullYear();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    useMockDataRef.current = useMockData;
  }, [useMockData]);

  const fetchStandings = () => {
    fetch("https://api.squiggle.com.au/?q=standings")
      .then((r) => r.json())
      .then(({ standings }) => {
        const map = standings.reduce((acc, s) => {
          acc[s.id] = s.rank;
          return acc;
        }, {});
        setStandingsMap(map);
      })
      .catch(() => setStandingsMap({}));
  };

  const refreshData = async () => {
    try {
      const now = Math.floor(Date.now() / 1000);
      const round = getCurrentRound(now);
      const seasonYear = getSeasonYear();
      setCurrentRound(round);
      const response = await fetch(
        `https://api.squiggle.com.au/?q=games;year=${seasonYear};round=${round}`,
      );
      const data = await response.json();

      const enriched = data.games.map((g) => {
        const ref = matchReference.find((r) => r.id === String(g.id));
        return { ...g, aflId: ref?.aflid || null };
      });

      setCurrentMatches(enriched);
      fetchStandings();
    } catch (err) {
      console.error("Error refreshing current matches:", err);
    }
  };

  const resetRefreshTimer = () => {
    if (refreshInterval.current) clearInterval(refreshInterval.current);

    refreshInterval.current = setInterval(() => {
      if (document.visibilityState === "visible" && !useMockDataRef.current) {
        console.log("[INTERVAL] Refreshing current round page data...");
        refreshData();
      } else {
        console.log("[INTERVAL] Skipped — tab not visible or using mock data");
      }
    }, 60000); // 60 seconds
  };

  useEffect(() => {
    resetRefreshTimer(); // start on mount

    const events = ["click", "keydown", "mousemove", "touchstart", "scroll"];
    events.forEach((ev) => document.addEventListener(ev, resetRefreshTimer));

    return () => {
      if (refreshInterval.current) clearInterval(refreshInterval.current);
      events.forEach((ev) =>
        document.removeEventListener(ev, resetRefreshTimer),
      );
    };
  }, []);

  useEffect(() => {
    fetchStandings();
    const cachedTeam = localStorage.getItem("selectedTeam");
    if (cachedTeam) {
      setSelectedTeam(teamNameMap[cachedTeam] || cachedTeam);
    }
  }, []);

  // New: toggle handler for mock data
  const handleMockToggle = (e) => {
    const on = e.target.checked;
    setUseMockData(on);
    localStorage.setItem("useMockData", on);
  };

  // Reordered mockCurrentGames: full-time first, then live, then upcoming
  const mockCurrentGames = [
    // Full-time games
    {
      id: 3,
      complete: 100,
      hteam: "Adelaide",
      ateam: "Brisbane Lions",
      hscore: 68,
      ascore: 75,
      hgoals: 10,
      hbehinds: 8,
      agoals: 11,
      abehinds: 9,
      timestr: "Full Time",
      venue: "Adelaide Oval",
      date: "2024-07-18T09:05:00Z",
    },
    {
      id: 4,
      complete: 100,
      hteam: "Carlton",
      ateam: "Collingwood",
      hscore: 82,
      ascore: 88,
      hgoals: 12,
      hbehinds: 10,
      agoals: 13,
      abehinds: 10,
      timestr: "Full Time",
      venue: "MCG",
      date: "2024-07-19T09:15:00Z",
    },

    // Live games
    {
      id: 1,
      complete: 50,
      hteam: "Geelong",
      ateam: "Western Bulldogs",
      hscore: 48,
      ascore: 95,
      hgoals: 7,
      hbehinds: 6,
      agoals: 14,
      abehinds: 11,
      timestr: "Q4 30:32",
      venue: "Kardinia Park",
      date: "2024-07-20T04:00:00Z",
    },
    {
      id: 2,
      complete: 30,
      hteam: "Port Adelaide",
      ateam: "Richmond",
      hscore: 116,
      ascore: 75,
      hgoals: 17,
      hbehinds: 14,
      agoals: 11,
      abehinds: 9,
      timestr: "Q3 15:45",
      venue: "Adelaide Oval",
      date: "2024-07-20T07:00:00Z",
    },

    // Upcoming games
    {
      id: 5,
      complete: 0,
      hteam: "Essendon",
      ateam: "Fremantle",
      hscore: 0,
      ascore: 0,
      hgoals: 0,
      hbehinds: 0,
      agoals: 0,
      abehinds: 0,
      timestr: "19h 35m",
      venue: "Etihad Stadium",
      date: "2024-07-20T09:00:00Z",
    },
    {
      id: 6,
      complete: 0,
      hteam: "Gold Coast",
      ateam: "Greater Western Sydney",
      hscore: 0,
      ascore: 0,
      hgoals: 0,
      hbehinds: 0,
      agoals: 0,
      abehinds: 0,
      timestr: "20h 05m",
      venue: "Metricon Stadium",
      date: "2024-07-20T09:30:00Z",
    },
    {
      id: 7,
      complete: 0,
      hteam: "Hawthorn",
      ateam: "Melbourne",
      hscore: 0,
      ascore: 0,
      hgoals: 0,
      hbehinds: 0,
      agoals: 0,
      abehinds: 0,
      timestr: "1d 1h",
      venue: "Waverley Park",
      date: "2024-07-21T03:30:00Z",
    },
    {
      id: 8,
      complete: 0,
      hteam: "North Melbourne",
      ateam: "St Kilda",
      hscore: 0,
      ascore: 0,
      hgoals: 0,
      hbehinds: 0,
      agoals: 0,
      abehinds: 0,
      timestr: "1d 3h",
      venue: "Blundstone Arena",
      date: "2024-07-21T06:00:00Z",
    },
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchCurrentMatches = async () => {
      try {
        const now = Math.floor(Date.now() / 1000);
        const round = getCurrentRound(now);
        const seasonYear = getSeasonYear();
        setCurrentRound(round);
        const response = await fetch(
          `https://api.squiggle.com.au/?q=games;year=${seasonYear};round=${round}`,
        );
        const data = await response.json();

        // enrich with afl website match id
        const enriched = data.games.map((g) => {
          const ref = matchReference.find((r) => r.id === String(g.id));
          return { ...g, aflId: ref?.aflid || null };
        });

        setCurrentMatches(enriched);
      } catch (err) {
        console.error("Error fetching current matches:", err);
      }
    };

    if (useMockData) {
      setCurrentMatches(mockCurrentGames);
      setCurrentRound(getCurrentRound(Math.floor(Date.now() / 1000)));
    } else {
      fetchCurrentMatches();
    }
  }, [useMockData]);

  const byeTeams = Object.keys(teamLogos)
    .filter(
      (team) =>
        !currentMatches.some((m) => m.hteam === team || m.ateam === team),
    )
    .sort((a, b) => a.localeCompare(b));

  const calculateRemainingTime = (date) => {
    const now = new Date();
    const gameDate = new Date(date);
    const secondsDiff = differenceInSeconds(gameDate, now);

    if (secondsDiff <= 0) {
      return null; // Game has started
    }

    const days = differenceInDays(gameDate, now);
    const hours = differenceInHours(gameDate, now) % 24;
    const minutes = differenceInMinutes(gameDate, now) % 60;

    if (days >= 1) {
      return `${days}d ${hours}h`;
    }
    if (hours >= 1) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  return (
    <div className={styles.currentRoundPage}>
      <h1 className={styles.currentRoundTitle}>Round {currentRound}</h1>
      <div className={styles.currentRoundContent}>
        {currentMatches.length > 0 ? (
          <>
            <div className={styles.matchCardsContainer}>
              {currentMatches.map((match) => {
                const isLive = match.complete > 0 && match.complete < 100;
                const isFullTime = match.complete === 100;
                const isUpcoming = !isLive && !isFullTime;

                const homeAbbrev = getTeamDisplayName(match.hteam);
                const awayAbbrev = getTeamDisplayName(match.ateam);
                const homeId = TEAM_INFO[homeAbbrev]?.id;
                const awayId = TEAM_INFO[awayAbbrev]?.id;

                const localDate = toZonedTime(
                  new Date(match.date),
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
                );
                const formattedDate = format(localDate, "EEE MMM dd");
                const formattedTime = format(localDate, "h:mm a");

                // decide what to show in the “time” slot
                let displayTime;
                if (isLive) {
                  displayTime = "Live";
                } else if (isFullTime) {
                  if (match.hscore === match.ascore) {
                    displayTime = "Draw";
                  } else {
                    const winnerKey =
                      match.hscore > match.ascore ? match.hteam : match.ateam;
                    const winner = getTeamDisplayName(winnerKey);
                    const margin = Math.abs(match.hscore - match.ascore);
                    displayTime = `${winner} won by ${margin}`;
                  }
                } else {
                  displayTime = formattedTime;
                }

                const remainingTime =
                  match.remainingTime || calculateRemainingTime(match.date);

                // determine winner for appending " W"
                const winnerKey =
                  match.hscore > match.ascore
                    ? match.hteam
                    : match.ascore > match.hscore
                      ? match.ateam
                      : null;
                const isHomeWinner = isFullTime && winnerKey === match.hteam;
                const isAwayWinner = isFullTime && winnerKey === match.ateam;

                const cardClass = [
                  styles.matchCard,
                  isLive && styles.liveCard,
                  isFullTime && styles.fullTimeCard,
                ]
                  .filter(Boolean)
                  .join(" ");

                const url = match.aflId
                  ? `https://www.afl.com.au/matches/${match.aflId}`
                  : null;

                const CardInner = (
                  <>
                    {/* Row 1 */}
                    <div className={styles.matchCardFirstRow}>
                      <span>{match.venue}</span>
                      <span className={styles.matchDate}>{formattedDate}</span>
                    </div>

                    {/* Row 2: Home team */}
                    <div className={styles.matchCardSecondRow}>
                      <div
                        className={`${styles.teamInfo} ${
                          getTeamDisplayName(match.hteam) === selectedTeam
                            ? styles.highlightedTeam
                            : ""
                        }`}
                      >
                        <img
                          src={teamLogos[match.hteam]}
                          alt={`${match.hteam} logo`}
                          className={styles.teamLogo}
                        />
                        <span className={styles.teamName}>
                          {getTeamDisplayName(match.hteam)}
                          {isHomeWinner && (
                            <span className={styles.winnerW}>W</span>
                          )}
                        </span>
                      </div>
                      <div className={styles.teamScores}>
                        {isUpcoming ? (
                          <span className={styles.homeTeamRank}>
                            {standingsMap[homeId]
                              ? formatOrdinal(standingsMap[homeId])
                              : "-"}
                          </span>
                        ) : (
                          <>
                            <span className={styles.teamGoals}>
                              ({match.hgoals || 0}.{match.hbehinds || 0})
                            </span>
                            <span className={styles.teamScore}>
                              {match.hscore}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Row 3: Away team */}
                    <div className={styles.matchCardThirdRow}>
                      <div
                        className={`${styles.teamInfo} ${
                          getTeamDisplayName(match.ateam) === selectedTeam
                            ? styles.highlightedTeam
                            : ""
                        }`}
                      >
                        <img
                          src={teamLogos[match.ateam]}
                          alt={`${match.ateam} logo`}
                          className={styles.teamLogo}
                        />
                        <span className={styles.teamName}>
                          {getTeamDisplayName(match.ateam)}
                          {isAwayWinner && (
                            <span className={styles.winnerW}>W</span>
                          )}
                        </span>
                      </div>
                      <div className={styles.teamScores}>
                        {isUpcoming ? (
                          <span className={styles.awayTeamRank}>
                            {standingsMap[awayId]
                              ? formatOrdinal(standingsMap[awayId])
                              : "-"}
                          </span>
                        ) : (
                          <>
                            <span className={styles.teamGoals}>
                              ({match.agoals || 0}.{match.abehinds || 0})
                            </span>
                            <span className={styles.teamScore}>
                              {match.ascore}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Row 4 */}
                    <div className={styles.matchCardFourthRow}>
                      <span>{remainingTime || match.timestr}</span>
                      <span className={styles.matchTime}>{displayTime}</span>
                    </div>
                  </>
                );

                return url ? (
                  <a
                    key={match.id}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cardClass}
                  >
                    {CardInner}
                  </a>
                ) : (
                  <div key={match.id} className={cardClass}>
                    {CardInner}
                  </div>
                );
              })}
            </div>

            {byeTeams.length > 0 && (
              <div className={styles.byeListContainer}>
                <span className={styles.byeHeader}>BYE ROUND</span>
                <div className={styles.byeTeams}>
                  {byeTeams.map((team, i) => (
                    <span key={team} className={styles.byeTeam}>
                      <img
                        src={teamLogos[team]}
                        alt={team}
                        className={styles.teamLogo}
                      />{" "}
                      {getTeamDisplayName(team)}
                      {i < byeTeams.length - 1 && "\u00A0\u00A0\u00A0\u00A0"}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* New: mock data toggle */}
            <div className={styles.mockToggleContainer}>
              <label>
                <input
                  type="checkbox"
                  checked={useMockData}
                  onChange={handleMockToggle}
                />{" "}
                Toggle dummy data
              </label>
            </div>
          </>
        ) : (
          <div className={styles.noDataContainer}>
            <img className={styles.whistle} src={whistle} alt="Whistle" />
            <p className={styles.noDataAlert}>
              No current round match data available
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentRoundPage;
